import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Content } from "@iventis/translations/content/typed-content";
import { Header4, Header5 } from "@iventis/styles/src/components/texts";
import { TreeBrowserComponent } from "@iventis/tree-browser";
import { inlineTextIconMargin } from "@iventis/styles/src/atomic-rules";
import { TreeBrowserHeaderComponent } from "@iventis/tree-browser/components/tree-browser-header";
import { useMachine } from "@xstate/react";
import React, { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Button, LinearProgress } from "@mui/material";
import { Theme } from "@emotion/react";
import { User } from "@iventis/domain-model/model/user";
import { UserGroupWithUsers } from "@iventis/domain-model/model/userGroupWithUsers";
import { customMediaQueryMax, customMediaQueryMin, screenSizeBreakpoints, styled } from "@iventis/styles";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { isAdmin, xstateDevTools } from "@iventis/utilities";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { ConfirmationDialogComponent, CustomDialog, SortMethod } from "@iventis/components";
import {
    CreateTeamComponent,
    peopleViewMachine,
    PeopleViewMachineEventType,
    AddPeopleModal,
    AddPeople,
    createTeamsNodeTree,
    isUserLeader,
    AdminTreeNodeType,
    createTeamNodeComponent,
    createUserNodeComponent,
    teamsViewMachine,
    TeamsViewMachineEvent,
    AdminPersonNode,
    TeamsNodeTree,
} from "@iventis/people";
import { toast } from "@iventis/toasts/src/toast";
import { authSlice, AuthState } from "@iventis/permissions/src/auth.slice";
import {
    addPeopleToTeam,
    createTeam,
    deleteTeam,
    getPeople,
    getTeams,
    postNewUserAndGroups,
    removePersonFromTeam,
    resetPasswords,
    updateTeamLeader,
} from "@iventis/permissions/src/people-api-functions";

const AddPersonTitle: React.FC<{ teamName: string; className?: string }> = ({ className, teamName }) => {
    const translate = useIventisTranslate();
    return (
        <div className={className} style={{ margin: "20px" }}>
            <Header4 style={{ marginBottom: "5px" }}>{translate(Content.people.addTeamMember)}</Header4>
            <Header5 style={{ fontWeight: "normal" }}>
                <FontAwesomeIcon icon={{ prefix: "far", iconName: "screen-users" }} style={{ marginRight: inlineTextIconMargin }} />
                {teamName}
            </Header5>
        </div>
    );
};

export const TeamsView: React.FC = () => {
    const translate = useIventisTranslate();

    const dispatch = useDispatch();

    const { projectAdmin, projectId } = useSelector((state: { auth: AuthState }) => state.auth.user);

    const [state, sendTeamsMachine] = useMachine(
        teamsViewMachine({
            addPeopleToTeam,
            createTeam,
            deleteTeam,
            getPeople,
            getTeams,
            postNewUserAndGroups: postNewUserAndGroups((data) => dispatch(authSlice.actions.setUsage({ usage: "adjustUsers", adjustment: data.length }))),
            removePersonFromTeam,
            resetPasswords,
            updateTeamLeader,
        }),
        {
            devTools: xstateDevTools,
            context: { translate, toast },
        }
    );
    const addPeopleMachine = useMachine(
        peopleViewMachine({
            postNewUserAndGroups: postNewUserAndGroups((data) => dispatch(authSlice.actions.setUsage({ usage: "adjustUsers", adjustment: data.length }))),
            getTeams,
            getPeople,
            toast,
            translate,
        }),
        { context: { projectId } }
    );
    const sendAddPeopleMachine = addPeopleMachine[1];

    useEffect(() => {
        sendAddPeopleMachine({ type: PeopleViewMachineEventType.CLEAR_SELECTION, payload: undefined });
    }, [state.context.teamForPersonAddition]);

    const nodeTree: TeamsNodeTree = useMemo(() => (state.context.teams ? createTeamsNodeTree(state.context.teams) : undefined), [state.context.teams]);

    const currentTeams = useMemo(() => state.context.teams, [state.context.teams]);

    const currentUser = useSelector((state: { auth: AuthState }) => state.auth.user, shallowEqual);

    const peopleNodeComponents = useMemo(
        () => ({
            [AdminTreeNodeType.Root]: undefined,
            [AdminTreeNodeType.User]: createUserNodeComponent<AdminPersonNode>({
                showCheckBoxColumn: false,
                showOverflowMenuColumn: true,
                showAdornmentColumn: true,
                getAdornment: ({ permission }) =>
                    isAdmin(permission.authorisation) ? (
                        <span>
                            <FontAwesomeIcon icon={{ prefix: "far", iconName: "crown" }} style={{ marginRight: inlineTextIconMargin, transform: "scale(0.9)" }} />
                            {translate(Content.people.teamLeader)}
                        </span>
                    ) : undefined,
                getOverflowOptions: ({ team, person, permission }) =>
                    currentUser.projectAdmin || isUserLeader(currentUser.userId, permission.resourceId, state.context.teams)
                        ? [
                              {
                                  text: translate(Content.people.removeFromTeamOverflowOption),
                                  action: () => sendTeamsMachine({ type: TeamsViewMachineEvent.SET_PERSON_FOR_TEAM_REMOVAL, payload: { team, person } }),
                              },
                              {
                                  text: isAdmin(permission.authorisation) ? Content.people.removeTeamLeader : Content.people.addTeamLeader,
                                  action: () => {
                                      // Invert current authorisation i.e. if admin, set to write and vice versa
                                      const auth = isAdmin(permission.authorisation) ? Authorisation.Write : Authorisation.Admin;
                                      sendTeamsMachine({
                                          type: TeamsViewMachineEvent.REQUEST_UPDATE_TEAM_LEADER,
                                          payload: { userId: person.id, teamId: permission.resourceId, authorisation: auth },
                                      });
                                  },
                              },
                          ]
                        : [],
            }),
            [AdminTreeNodeType.Group]: createTeamNodeComponent({
                clickAddPerson: (team: UserGroupWithUsers) => {
                    sendTeamsMachine({ type: TeamsViewMachineEvent.TEAM_SELECTED_FOR_PERSON_ADDITION, payload: { team } });
                },
                clickDeleteTeam: (team: UserGroupWithUsers) => {
                    sendTeamsMachine({ type: TeamsViewMachineEvent.DELETE_TEAM, payload: { team } });
                },
                expand: (id) => sendTeamsMachine({ type: TeamsViewMachineEvent.EXPAND, payload: id }),
                collapse: (id) => sendTeamsMachine({ type: TeamsViewMachineEvent.COLLAPSE, payload: id }),
                canAddUser: projectAdmin,
            }),
        }),
        [currentTeams, translate]
    );

    const alphabeticalSortMethod = useMemo(() => translate(Content.people.teamsAlphabetical), [translate]);

    const sortMethods: SortMethod[] = [{ displayName: alphabeticalSortMethod, id: `nameAsc` }];

    const addPeople = (people: User[]) => {
        sendTeamsMachine({ type: TeamsViewMachineEvent.ADD_PEOPLE_TO_TEAM, payload: { teamId: state.context.teamForPersonAddition.id, people } });
    };

    const handleNewTeamClick = () => {
        sendTeamsMachine({ type: TeamsViewMachineEvent.CREATE_TEAM });
    };

    return (
        <>
            <StyledContainer showPersonSelection={state.context.teamForPersonAddition !== undefined}>
                <PeoplePageTopContent>
                    <PeoplePageHeader>
                        <Header5>
                            <FontAwesomeIcon icon={{ prefix: "far", iconName: "screen-users" }} /> {translate(Content.people.teams)}
                        </Header5>
                        <div className="team-functionality">
                            <StyledButton variant="outlined" color="primary" onClick={handleNewTeamClick}>
                                {translate(Content.people.createNewTeam)}
                            </StyledButton>
                        </div>
                    </PeoplePageHeader>
                </PeoplePageTopContent>
                {state.matches("teamsList.teamsLoading") && <LinearProgress />}
                {(state.matches("teamsList.idle") || state.matches("teamsList.createTeam") || state.matches("teamsList.deleteTeam")) && (
                    <div style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
                        <StyledTreeBrowserHeaderComponent
                            selectAllValue={false}
                            selectAll={() => undefined}
                            sortMethods={sortMethods}
                            // eslint-disable-next-line @typescript-eslint/no-empty-function
                            onSortMethodChange={() => {}}
                            selectedSortMethod={sortMethods[0]}
                            hideSelectAll={false}
                        />
                        <div style={{ overflowY: "auto", flex: "1 1" }}>
                            <StyledTreeBrowserComponent
                                className="tree-browser"
                                node={nodeTree}
                                displayRootNode={false}
                                components={peopleNodeComponents}
                                openNodes={state.context.expandedTeams}
                            />
                        </div>
                    </div>
                )}
                {state.context.teamForPersonAddition !== undefined && (
                    <AddPeople
                        isSubmitting={state.matches("addingPeopleToTeam")}
                        machineInstance={addPeopleMachine}
                        className="small-device-add-people"
                        title={<AddPersonTitle teamName={state.context.teamForPersonAddition.name} className="small-device-add-people" />}
                        addPeople={addPeople}
                        unavailablePersonIds={state.context.teamForPersonAddition?.userMembers.map(({ id }) => id)}
                        apiCallbacks={{
                            postNewUserAndGroups: postNewUserAndGroups((data) => dispatch(authSlice.actions.setUsage({ usage: "adjustUsers", adjustment: data.length }))),
                            getPeople,
                            getTeams,
                        }}
                        toast={toast}
                        translate={translate}
                    />
                )}
                <CustomDialog open={state.matches("teamsList.createTeam")} onClose={() => sendTeamsMachine({ type: TeamsViewMachineEvent.CANCEL_CREATE_TEAM })}>
                    <CreateTeamComponent
                        cancel={() => sendTeamsMachine({ type: TeamsViewMachineEvent.CANCEL_CREATE_TEAM })}
                        submit={(name: string) => sendTeamsMachine({ type: TeamsViewMachineEvent.SAVE_NEW_TEAM, payload: name })}
                        header={translate(Content.people.createANewTeam)}
                        isSubmitting={state.matches("createTeam.saveNewTeam ")}
                    />
                </CustomDialog>
            </StyledContainer>
            <ConfirmationDialogComponent
                show={state.matches("teamLeader.updateTeamLeaderRequested") || state.matches("teamLeader.updatingTeamLeader")}
                title={translate(isAdmin(state.context?.personToUpdateLeader?.authorisation) ? Content.people.addTeamLeader : Content.people.removeTeamLeader)}
                message={translate(isAdmin(state.context?.personToUpdateLeader?.authorisation) ? Content.people.addTeamLeaderMessage : Content.people.removeTeamLeaderMessage)}
                confirmText={translate(Content.common.buttons.confirm)}
                cancelText={translate(Content.common.buttons.close)}
                isSubmitting={state.matches("teamLeader.updatingTeamLeader")}
                handleCancel={() => {
                    sendTeamsMachine(TeamsViewMachineEvent.CANCEL_TEAM_LEADER);
                }}
                handleConfirm={() => {
                    const payload = state.context.personToUpdateLeader;
                    sendTeamsMachine({
                        type: TeamsViewMachineEvent.UPDATE_TEAM_LEADER,
                        payload: { ...payload },
                    });
                }}
            />
            <ConfirmationDialogComponent
                show={state.context.personRemovalFromTeamSelection !== undefined}
                title={translate(Content.people.removeFromTeamTitle)}
                message={`${translate(Content.people.removeFromTeamMessage1)}\n${translate(Content.people.removeFromTeamMessage2)}`}
                confirmText={translate(Content.common.buttons.confirm)}
                cancelText={translate(Content.common.buttons.close)}
                isSubmitting={state.matches("removeFromTeam.removingPersonFromTeam")}
                handleCancel={() => {
                    sendTeamsMachine({ type: TeamsViewMachineEvent.SET_PERSON_FOR_TEAM_REMOVAL, payload: undefined });
                }}
                handleConfirm={async () => {
                    sendTeamsMachine({ type: TeamsViewMachineEvent.REMOVE_PERSON_FROM_TEAM, payload: state.context.personRemovalFromTeamSelection });
                }}
            />
            {state.matches("teamsList.deleteTeam") && (
                <ConfirmationDialogComponent
                    show={state.matches("teamsList.deleteTeam")}
                    title={translate(Content.people.deleteTeamTitle)}
                    message={translate(Content.people.deleteTeamMessage, { team: state.context.teamToDelete.name })}
                    confirmText={translate(Content.common.buttons.confirm)}
                    cancelText={translate(Content.common.buttons.close)}
                    isSubmitting={state.matches("deleteTeam.deleteSelectedTeam")}
                    handleCancel={() => {
                        sendTeamsMachine({ type: TeamsViewMachineEvent.CANCEL_DELETE_TEAM });
                    }}
                    handleConfirm={async () => {
                        sendTeamsMachine({ type: TeamsViewMachineEvent.CONFIRM_DELETE_TEAM });
                    }}
                />
            )}
            <AddPeopleModal
                isSubmitting={state.matches("teamsList.addingPeopleToTeam")}
                machineInstance={addPeopleMachine}
                open={state.context.teamForPersonAddition !== undefined}
                title={<AddPersonTitle teamName={state.context.teamForPersonAddition?.name} />}
                addPeople={addPeople}
                unavailablePersonIds={state.context.teamForPersonAddition?.userMembers.map(({ id }) => id)}
                apiCallbacks={{
                    postNewUserAndGroups: postNewUserAndGroups((data) => dispatch(authSlice.actions.setUsage({ usage: "adjustUsers", adjustment: data.length }))),
                    getTeams,
                    getPeople,
                }}
                toast={toast}
                translate={translate}
            />
        </>
    );
};

const StyledTreeBrowserHeaderComponent = styled(TreeBrowserHeaderComponent)`
    flex: 0 0;
`;

const StyledTreeBrowserComponent = styled(TreeBrowserComponent)`
    flex-shrink: 1;

    .node-type-User {
        margin-left: 40px;
    }

    .node-type-User:not(:last-of-type) {
        border-bottom: 1px solid ${({ theme }: { theme: Theme }) => theme.otherColors.separatorDark};
    }
`;

export const PeoplePageTopContent = styled.div`
    padding: 10px 20px;
    gap: 10px;
`;

export const PeoplePageHeader = styled.div`
    display: flex;
    align-items: center;
    height: 50px;
    .team-functionality {
        display: flex;
        flex-grow: 1;
        justify-content: flex-end;
    }
`;

const StyledButton = styled(Button)`
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.core};
    border-color: ${({ theme }: { theme: Theme }) => theme.primaryColors.subdued50};
    :hover {
        background-color: ${({ theme }: { theme: Theme }) => theme.shades.four};
        border-color: ${({ theme }: { theme: Theme }) => theme.primaryColors.subdued50};
    }
`;

const StyledContainer = styled.div<{ showPersonSelection: boolean }>`
    flex-grow: 1;
    height: 100%;
    min-width: 0;

    display: flex;
    flex-direction: column;

    ${({ showPersonSelection }) =>
        showPersonSelection
            ? `
        ${customMediaQueryMax(screenSizeBreakpoints.large)} {
            > *:not(.small-device-add-people) {
                display: none;
            }
        }

        ${customMediaQueryMin(screenSizeBreakpoints.large)} {
            .small-device-add-people {
                display: none;
            }
        }
        `
            : ``}
`;
